/**
 * <ReceivedTab> — recipient-side list of shares the caller has received.
 *
 * `external-sharing` Phase 4 Session 4.1 (2026-05-09).
 *
 * 4 states (mirrors `<SentTab>`): loading skeleton, error retry, empty
 * placeholder, success. Success path renders rows in a single section —
 * recipients only see active shares per Phase 1's
 * `shared_links_recipient_select` RLS policy gating `status='active'`.
 *
 * Sort: `(recipientFirstViewedAt NULLS LAST, createdAt DESC)`. Unread rows
 * come first; viewed rows fall through createdAt-DESC.
 *
 * Mark-read mount-effect: `useEffect` watching `[data]` fires `useMarkRead`
 * for each row where `recipientFirstViewedAt === null`. The ref-set guard
 * (`markedRef`) prevents the post-invalidate refetch from re-firing the
 * mutation on rows we've already marked. Mutations run in parallel via
 * `Promise.all`; on settled, the hook's `onSettled` invalidates the
 * `received()` + `unreadCount()` caches so the bell badge clears.
 */
import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
  useReceivedShares,
  useMarkRead,
  useForkShare,
} from '@/hooks/use-shares';
import type { SharedLinkResponse } from '@/lib/shares-api';
import { posthog } from '@/lib/posthog';
import { ReceivedShareRow, RECEIVED_SHARE_ROW_GRID } from './ReceivedShareRow';
import { SharesEmptyState, SharesLoadingSkeleton } from './SharesEmptyState';
import { SharesTableHeader } from './SharesTableHeader';

// NEW Phase 3 § 3.4 — sessionStorage key for first-open dedup.
// Tracks which share IDs have already fired `share_received_opened` in this
// tab session. Per-tab-session only (not user-scoped) — ephemeral UX state.
const FIRST_OPEN_KEY = 'arcanum_share_received_opened_set';

function trackShareReceivedOpened(shareId: string): void {
  try {
    const raw = sessionStorage.getItem(FIRST_OPEN_KEY);
    const opened = new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
    if (!opened.has(shareId)) {
      posthog.capture('share_received_opened', { share_id: shareId });
      opened.add(shareId);
      sessionStorage.setItem(
        FIRST_OPEN_KEY,
        JSON.stringify(Array.from(opened))
      );
    }
  } catch {
    /* sessionStorage may be disabled in some private-mode browsers */
  }
}

const RECEIVED_COLUMNS = [
  { label: 'Sender' },
  { label: 'Deal' },
  { label: 'Received' },
  { label: '' }, // actions column — buttons speak for themselves
];

export interface ReceivedTabProps {
  highlightId: string | undefined;
}

function sortReceived(shares: SharedLinkResponse[]): SharedLinkResponse[] {
  // (recipientFirstViewedAt NULLS LAST, createdAt DESC).
  // Unread (null) ranks higher than any viewed row. Within each band,
  // newest createdAt comes first.
  return [...shares].sort((a, b) => {
    const aUnread = a.recipientFirstViewedAt === null;
    const bUnread = b.recipientFirstViewedAt === null;
    if (aUnread !== bUnread) return aUnread ? -1 : 1;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

export function ReceivedTab({ highlightId }: ReceivedTabProps) {
  const { data, isLoading, isError, refetch } = useReceivedShares();
  const markReadMutation = useMarkRead();
  const forkShareMutation = useForkShare();

  // Track shares we've already marked-read this mount. The mount-effect
  // re-runs on every `data` resolve (including the post-invalidate refetch
  // triggered by the prior batch's onSettled), so without a ref-set guard
  // we'd re-POST `mark-read` for every row indefinitely.
  const markedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!data || data.length === 0) return;
    const toMark = data.filter(
      (s) => s.recipientFirstViewedAt === null && !markedRef.current.has(s.id)
    );
    if (toMark.length === 0) return;
    for (const s of toMark) markedRef.current.add(s.id);

    // ADR 0008 — Promise.allSettled gives per-item resolution so we can:
    // (1) roll back only the failed IDs (not the whole batch), and
    // (2) include the actual failed share IDs in the Sentry capture.
    void Promise.allSettled(
      toMark.map((s) => markReadMutation.mutateAsync(s.id))
    ).then((results) => {
      const failedIds = toMark
        .filter((_, i) => results[i].status === 'rejected')
        .map((s) => s.id);
      if (failedIds.length === 0) return;
      // Granular rollback: only failed IDs revert; successful marks stay in
      // the ref-set so they are not re-attempted on the next data resolve.
      for (const id of failedIds) markedRef.current.delete(id);
      // Capture site (Phase 3 § 3.1) — NOT rate-limited (low-frequency,
      // user-action-driven event; not a storm pattern per ADR 0008).
      Sentry.captureMessage('mark_read_batch_partial_failure', {
        level: 'warning',
        tags: { surface: 'shares-received-mark-read' },
        extra: {
          failed_share_ids: failedIds,
          total_attempted: toMark.length,
        },
      });
    });
    // We deliberately depend on `data` only — `markReadMutation` is stable
    // across renders (TanStack Query mutation hook).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Scroll highlighted row into view when the data resolves and the target
  // is present. Mirrors `<SentTab>`'s pattern.
  useEffect(() => {
    if (!highlightId || !data) return;
    if (!data.find((s) => s.id === highlightId)) return;
    const id = window.setTimeout(() => {
      const el = document.getElementById(`share-row-${highlightId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return () => window.clearTimeout(id);
  }, [highlightId, data]);

  if (isLoading) {
    return <SharesLoadingSkeleton testid="shares-received-loading" />;
  }

  if (isError) {
    return (
      <SharesEmptyState
        variant={{ kind: 'received-error', onRetry: () => void refetch() }}
      />
    );
  }

  const shares = data ?? [];

  if (shares.length === 0) {
    return <SharesEmptyState variant={{ kind: 'received-empty' }} />;
  }

  const sorted = sortReceived(shares);
  const handleFork = (shareId: string): void => {
    forkShareMutation.mutate(shareId);
  };
  const forkingId = forkShareMutation.isPending
    ? forkShareMutation.variables ?? null
    : null;

  return (
    <div data-testid="shares-received-list" className="flex flex-col">
      <SharesTableHeader
        gridClass={RECEIVED_SHARE_ROW_GRID}
        columns={RECEIVED_COLUMNS}
        testid="shares-received-header"
      />
      <div className="flex flex-col gap-2 mt-2">
        {sorted.map((share) => (
          // NEW Phase 3 § 3.4 — fire share_received_opened on first interaction
          // with any row element (Open link, Fork, Versions). Event delegation
          // via the wrapper div avoids modifying ReceivedShareRow's prop API.
          <div
            key={share.id}
            onClick={() => trackShareReceivedOpened(share.id)}
            role="none"
          >
            <ReceivedShareRow
              share={share}
              highlighted={share.id === highlightId}
              onFork={handleFork}
              forking={forkingId === share.id}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
